import type { EndModuleSide, PlannerProject, WardrobeModule } from "../types";
import { cabinetBaseHeightMm, installationSpanMm } from "./installation";
import { endModuleForTargetSide } from "./endModules";
import { isStackedModule, materializeWallOffsets } from "./layout";

/**
 * Зеркало на открытой боковине шкафа — обычное решение для прихожей: зеркало
 * клеится на боковину, которая смотрит в комнату. Бывает только на крайних
 * боковинах композиции: если к боковине примыкает соседний корпус или торцевые
 * полки, зеркала не видно, и в заказ оно не идёт.
 *
 * У углового шкафа «левая» и «правая» — это торцы его плеч, как их видно из
 * комнаты: у левого углового слева торец плеча вдоль стены, справа — плечо,
 * к которому обычно примыкает следующая секция.
 */
export type SideMirrorSide = EndModuleSide;

/** Отступ зеркала от кромок боковины со всех сторон. */
export const SIDE_MIRROR_MARGIN_MM = 30;
export const SIDE_MIRROR_THICKNESS_MM = 4;
/** Стык ближе этого считается примыканием соседнего корпуса. */
const TOUCH_TOLERANCE_MM = 5;

export const supportsSideMirror = (module: Pick<WardrobeModule, "type">) =>
  module.type === "single" || module.type === "double" || module.type === "corner45";

/** Сохранённые стороны без повторов и мусора; порядок — слева направо. */
export const normalizeSideMirrors = (module: Pick<WardrobeModule, "type" | "sideMirrors">): SideMirrorSide[] | undefined => {
  if (!supportsSideMirror(module) || !Array.isArray(module.sideMirrors)) return undefined;
  const sides = (["left", "right"] as const).filter(side => module.sideMirrors!.includes(side));
  return sides.length ? sides : undefined;
};

/** Чем закрыта боковина, если закрыта. */
export function sideMirrorBlocker(
  project: PlannerProject,
  module: WardrobeModule,
  side: SideMirrorSide
): "neighbour" | "end" | null {
  if (endModuleForTargetSide(project, module.id, side)) return "end";
  const offsets = materializeWallOffsets(project);
  const start = offsets.get(module.id);
  if (start === undefined) return null;
  const finish = start + installationSpanMm(module);
  const touches = project.modules.some(other => {
    if (other.id === module.id || other.type === "end" || isStackedModule(other)) return false;
    const otherStart = offsets.get(other.id);
    if (otherStart === undefined) return false;
    const otherFinish = otherStart + installationSpanMm(other);
    return side === "left"
      ? Math.abs(otherFinish - start) <= TOUCH_TOLERANCE_MM
      : Math.abs(otherStart - finish) <= TOUCH_TOLERANCE_MM;
  });
  return touches ? "neighbour" : null;
}

/** Зеркала, которые реально видны и идут в производство. */
export const activeSideMirrors = (project: PlannerProject, module: WardrobeModule): SideMirrorSide[] =>
  (normalizeSideMirrors(module) ?? []).filter(side => !sideMirrorBlocker(project, module, side));

/** Размер зеркала по боковине, мм: ширина — по глубине корпуса, высота — над цоколем. */
export const sideMirrorSize = (module: WardrobeModule) => ({
  width: Math.max(100, module.depth - SIDE_MIRROR_MARGIN_MM * 2),
  height: Math.max(200, module.height - cabinetBaseHeightMm(module) - SIDE_MIRROR_MARGIN_MM * 2),
  /** Нижняя кромка от пола. */
  bottom: cabinetBaseHeightMm(module) + SIDE_MIRROR_MARGIN_MM
});

export const sideMirrorSideName = (side: SideMirrorSide) => side === "left" ? "слева" : "справа";
